import React, { useState } from "react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const METRICS = ["Median Price ($K)", "Sales Volume", "Avg DOM", "List-to-Sale %"];

const HEATMAP_DATA = {
  "Median Price ($K)": [
    [222, 228, 237, 248, 258, 268, 272, 268, 248, 238, 228, 220],
    [215, 221, 230, 241, 251, 261, 265, 261, 241, 231, 221, 213],
    [208, 214, 223, 234, 244, 254, 258, 254, 234, 224, 214, 206],
  ],
  "Sales Volume": [
    [268, 305, 362, 425, 448, 462, 418, 392, 348, 318, 278, 242],
    [255, 290, 345, 405, 428, 442, 398, 374, 332, 304, 265, 231],
    [242, 276, 328, 386, 408, 421, 379, 356, 316, 289, 252, 220],
  ],
  "Avg DOM": [
    [44, 40, 34, 28, 22, 18, 20, 23, 28, 33, 39, 46],
    [46, 42, 36, 30, 24, 20, 22, 25, 30, 35, 41, 48],
    [48, 44, 38, 32, 26, 22, 24, 27, 32, 37, 43, 50],
  ],
  "List-to-Sale %": [
    [97.4, 97.9, 98.5, 99.2, 99.9, 100.3, 100.1, 99.7, 98.9, 98.3, 97.7, 97.1],
    [97.0, 97.5, 98.1, 98.8, 99.5, 99.9, 99.7, 99.3, 98.5, 97.9, 97.3, 96.7],
    [96.6, 97.1, 97.7, 98.4, 99.1, 99.5, 99.3, 98.9, 98.1, 97.5, 96.9, 96.3],
  ],
};

const YEARS = ["2025", "2024", "2023"];

const getColor = (value, metric, allValues) => {
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const pct = (value - min) / (max - min);

  if (metric === "Avg DOM") {
    // Lower DOM = hotter market = green
    const inv = 1 - pct;
    if (inv > 0.75) return "bg-emerald-500 text-white";
    if (inv > 0.5) return "bg-emerald-300 text-emerald-900";
    if (inv > 0.25) return "bg-amber-200 text-amber-900";
    return "bg-red-200 text-red-900";
  }

  if (pct > 0.75) return "bg-emerald-500 text-white";
  if (pct > 0.5) return "bg-emerald-300 text-emerald-900";
  if (pct > 0.25) return "bg-amber-200 text-amber-900";
  return "bg-slate-200 text-slate-700";
};

const formatVal = (v, metric) => {
  if (metric === "Median Price ($K)") return `$${v}K`;
  if (metric === "List-to-Sale %") return `${v?.toFixed(1)}%`;
  return v;
};

export default function SeasonalHeatmap() {
  const [activeMetric, setActiveMetric] = useState("Median Price ($K)");
  const [hoveredCell, setHoveredCell] = useState(null);

  const data = HEATMAP_DATA?.[activeMetric];
  const allValues = data?.flat();

  return (
    <div className="card-base flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">Seasonal Market Patterns</h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">Month-over-month heatmap with 3-year comparison</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {METRICS?.map(m => (
            <button
              key={m}
              onClick={() => setActiveMetric(m)}
              className={`px-2.5 py-1 rounded-lg text-xs font-caption font-medium transition-standard ${
                activeMetric === m
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr>
              <th className="text-left text-xs font-caption font-medium text-muted-foreground pb-2 w-12">Year</th>
              {MONTHS?.map(m => (
                <th key={m} className="text-center text-xs font-caption font-medium text-muted-foreground pb-2 px-0.5">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {YEARS?.map((year, yi) => (
              <tr key={year}>
                <td className="text-xs font-caption font-semibold text-foreground pr-2 py-1">{year}</td>
                {MONTHS?.map((month, mi) => {
                  const val = data?.[yi]?.[mi];
                  const colorClass = getColor(val, activeMetric, allValues);
                  const cellKey = `${yi}-${mi}`;
                  return (
                    <td key={month} className="px-0.5 py-1">
                      <div
                        className={`relative flex items-center justify-center rounded-md text-xs font-mono-data font-medium
                          h-8 cursor-default transition-standard ${colorClass}
                          ${hoveredCell === cellKey ? "ring-2 ring-primary ring-offset-1" : ""}`}
                        onMouseEnter={() => setHoveredCell(cellKey)}
                        onMouseLeave={() => setHoveredCell(null)}
                        title={`${year} ${month}: ${formatVal(val, activeMetric)}`}
                      >
                        <span className="text-[10px] leading-none">{formatVal(val, activeMetric)}</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border">
        <span className="text-xs font-caption text-muted-foreground">Scale:</span>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-500" />
          <span className="text-xs font-caption text-muted-foreground">High</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-300" />
          <span className="text-xs font-caption text-muted-foreground">Above Avg</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-amber-200" />
          <span className="text-xs font-caption text-muted-foreground">Below Avg</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-slate-200" />
          <span className="text-xs font-caption text-muted-foreground">Low</span>
        </div>
        <span className="ml-auto text-xs font-caption text-muted-foreground italic">
          {activeMetric === "Avg DOM" ? "* Lower DOM = greener (faster market)" : ""}
        </span>
      </div>
    </div>
  );
}